/* ═══════════════════════════════════════════════════════
   SwipeHome — Map View lazy loader (Feature 4d)
   Thin wrapper around ./mapView.js that defers loading
   the Leaflet + markercluster chunk until the user first
   taps the Map view. Every caller (feed.ts, listings.ts,
   main.ts) imports from HERE instead of './mapView.js'
   so Vite can code-split mapView.ts (and its ~90 KB of
   Leaflet deps) out of the main bundle.

   Functions that genuinely need the module before they can
   do work (showMap) return a Promise and await the import.
   Functions that are no-ops until the map has been booted
   at least once (onFeedRefreshed, onLikesRefreshed,
   hideAllPreviewSheets, window handlers) check the cached
   module reference and silently no-op if nothing's loaded
   yet — there's nothing to refresh/close on a map that
   doesn't exist.
   ═══════════════════════════════════════════════════════ */

import type { MapKey } from './mapView.js';
export type { MapKey } from './mapView.js';

// The loaded module is cached after the first dynamic import.
// A concurrent-load promise dedups multiple calls during the
// ~200-800ms window while the chunk is downloading.
type MapViewModule = typeof import('./mapView.js');
let _loaded: MapViewModule | null = null;
let _loading: Promise<MapViewModule> | null = null;

async function ensureLoaded(): Promise<MapViewModule> {
  if (_loaded) return _loaded;
  if (_loading) return _loading;
  _loading = import('./mapView.js').then((mod) => {
    _loaded = mod;
    _loading = null;
    return mod;
  });
  return _loading;
}

/**
 * Boot or refresh the map for a given context. First call triggers
 * the chunk download; subsequent calls resolve immediately from
 * cache. Callers should await this so the map is interactable
 * before they poke at it.
 */
export async function showMap(key: MapKey): Promise<void> {
  const mod = await ensureLoaded();
  return mod.showMap(key);
}

/**
 * Called by feed.ts after state.feed changes. No-op if the map
 * hasn't been booted yet — there's nothing to refresh.
 */
export function onFeedRefreshed(): void {
  _loaded?.onFeedRefreshed();
}

/**
 * Called by listings.ts loadLikes() after a list load completes.
 * No-op if the likes map hasn't been booted yet.
 */
export function onLikesRefreshed(): void {
  _loaded?.onLikesRefreshed();
}

/**
 * Dismiss any open preview sheet in either map context. Safe to
 * call before the module has loaded — nothing to dismiss.
 */
export function hideAllPreviewSheets(): void {
  _loaded?.hideAllPreviewSheets();
}

// ── Inline onclick handlers ──
// These are called from HTML strings that mapView.ts itself
// generates, so by the time any of them fires the module MUST
// already be loaded. We still guard with optional-chaining in
// case of a pathological race (e.g. a stale sheet left in the
// DOM after a hot reload).

export function openMapDetail(listingId: string): void {
  _loaded?.openMapDetail(listingId);
}

export function closeMapPreview(): void {
  _loaded?.closeMapPreview();
}

export function mapSwipe(listingId: string, direction: 'left' | 'right'): Promise<void> | void {
  return _loaded?.mapSwipe(listingId, direction);
}

// Feature 4f — draw boundary
export function toggleDrawMode(): void {
  _loaded?.toggleDrawMode();
}

export function clearDrawBoundary(): void {
  _loaded?.clearDrawBoundary();
}

// Tier 1 — unsave, search/sort, locate-me
export function unlikeListing(listingId: string): Promise<void> | void {
  return _loaded?.unlikeListing(listingId);
}

export function likesDrawerSearch(query: string): void {
  _loaded?.likesDrawerSearch(query);
}

export function likesDrawerSort(value: string): void {
  _loaded?.likesDrawerSort(value);
}

export function locateMe(): void {
  _loaded?.locateMe();
}

// Redundancy #2 Option 2 — pipeline actions on Watchlist cards
export function openPipelineActions(
  listingId: string, address: string, currentStatus: string,
): Promise<void> | void {
  return _loaded?.openPipelineActions(listingId, address, currentStatus);
}

export function setDealPipelineStatus(listingId: string, status: string): Promise<void> | void {
  return _loaded?.setDealPipelineStatus(listingId, status);
}
